/**
 * AK-389 — вкладка «Осмотры» в карточке поля. С 16.08.2026 (founder «делаем а
 * сначала») веб умеет и создавать осмотр — кнопка «Записать осмотр» рядом со
 * списком, гейт по праву `scoutings:C` (см. `InspectionCreateSheet.tsx`).
 *
 * Строка: дата · автор · фенофаза · сводка находок (или зелёное «проблем нет») ·
 * число фото · чип источника. Новые сверху (backend уже ORDER BY inspected_at
 * DESC). Клик по строке → деталка в правом Sheet.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, Plus, RotateCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { PERM, useCan } from '@/lib/auth/usePermissions';
import { InspectionDetailSheet } from './InspectionDetailSheet';
import { InspectionCreateSheet } from './InspectionCreateSheet';
import {
  findingsSummary,
  formatInspectionDate,
  inspectorLabel,
  phaseLabel,
  photoCount,
  sourceChip,
  type Inspection,
} from './inspectionLogic';

interface ContourGeo {
  id: string;
  contour_type: string;
  geojson?: { type?: string; coordinates?: unknown } | null;
}

// Порядок предпочтения контура для мини-карты: эталон/рабочий/legacy → любой.
// AK-517 / ADR-061: 'planting' убран (мёртвый тип); 'legacy' — синтетический
// ряд фолбэка AK-176 из fields.geometry.
const CONTOUR_PRIORITY = ['reference', 'working', 'legacy'];

function pickFieldGeojson(contours?: ContourGeo[] | null) {
  if (!contours?.length) return null;
  const withGeo = contours.filter((c) => c.geojson?.coordinates);
  if (!withGeo.length) return null;
  for (const type of CONTOUR_PRIORITY) {
    const hit = withGeo.find((c) => c.contour_type === type);
    if (hit) return hit.geojson ?? null;
  }
  return withGeo[0].geojson ?? null;
}

export function FieldInspectionsTab({ fieldId }: { fieldId: string }) {
  const [selected, setSelected] = useState<Inspection | null>(null);
  const [creating, setCreating] = useState(false);
  const canCreate = useCan(PERM.scoutingWrite, 'c');

  const {
    data: inspections,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['field-inspections', fieldId],
    queryFn: () =>
      api.get<Inspection[]>(
        `/api/v1/inspections?field_id=${fieldId}&limit=200`,
      ),
  });

  // Контур поля для мини-карты деталки — тянем один раз, лениво (не блокирует
  // список; деталка переживёт его отсутствие, покажет только точку).
  const { data: contours } = useQuery({
    queryKey: ['field-contour-geo', fieldId],
    queryFn: () =>
      api.get<ContourGeo[]>(
        `/api/v1/fields/${fieldId}/contours?include_geojson=true`,
      ),
    staleTime: 5 * 60_000,
  });
  const fieldGeojson = useMemo(() => pickFieldGeojson(contours), [contours]);
  const rows = inspections ?? [];

  // Кнопка «Записать осмотр» — над списком в ЛЮБОМ состоянии (даже если список
  // ещё грузится / упал): создание не должно ждать успешного чтения.
  const header = canCreate && (
    <div className="mb-3 flex justify-end">
      <Button type="button" size="sm" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        Записать осмотр
      </Button>
    </div>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Card>
        <CardContent className="space-y-2 p-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  } else if (isError) {
    body = (
      <Card>
        <CardContent className="flex flex-col items-start gap-3 p-6">
          <p className="text-muted-foreground">Не удалось загрузить осмотры.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RotateCw className="mr-2 h-4 w-4" />
            Повторить
          </Button>
        </CardContent>
      </Card>
    );
  } else if (!rows.length) {
    body = (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 p-12 text-center">
          <Camera className="h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="text-muted-foreground">
            {canCreate
              ? 'Осмотров пока нет — запишите первый.'
              : 'Осмотров пока нет.'}
          </p>
        </CardContent>
      </Card>
    );
  } else {
    body = (
      <>
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border" data-testid="inspections-list">
              {rows.map((insp) => {
                const chip = sourceChip(insp);
                const summary = findingsSummary(insp);
                const phase = phaseLabel(insp);
                const nPhotos = photoCount(insp);
                const meta: React.ReactNode[] = [];
                if (phase) meta.push(<span key="phase">{phase}</span>);
                if (summary.tone === 'ok') {
                  meta.push(
                    <span key="ok" className="text-primary">
                      проблем нет
                    </span>,
                  );
                } else if (summary.tone === 'problem') {
                  meta.push(<span key="find">{summary.text}</span>);
                }
                if (nPhotos > 0) {
                  meta.push(
                    <span key="photos">
                      {nPhotos} {nPhotos === 1 ? 'фото' : 'фото'}
                    </span>,
                  );
                }
                return (
                  <li key={insp.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(insp)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">
                          {formatInspectionDate(insp.inspected_at)} ·{' '}
                          {inspectorLabel(insp)}
                        </div>
                        {meta.length > 0 && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                            {meta.map((m, i) => (
                              <span key={i} className="flex items-center gap-x-2">
                                {i > 0 && <span aria-hidden>·</span>}
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Badge variant={chip.variant} className="shrink-0">
                        {chip.label}
                      </Badge>
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        {isFetching && !isLoading && (
          <div className="mt-2 text-right text-xs text-muted-foreground">
            Обновление…
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {header}
      {body}

      <InspectionDetailSheet
        inspection={selected}
        fieldGeojson={fieldGeojson}
        onClose={() => setSelected(null)}
      />

      {canCreate && (
        <InspectionCreateSheet fieldId={fieldId} open={creating} onOpenChange={setCreating} />
      )}
    </>
  );
}

export default FieldInspectionsTab;
